import { Project } from '@rappider/rappider-sdk';
import {
  HeadingType,
  IconType,
  NgZorroIconTheme,
  ButtonSize,
  TextMode,
  CardOneComponentConfig
} from '@rappider/rappider-components/utils';
import { defaultToolbarTitleHeadingSize } from '@rappider/shared/definitions';
import { ABSTRACT_IMAGES } from '@rappider/shared/data';

/**
 * Interface for card creation options
 */
export interface CardItemOptions {
  showPreviewButton?: boolean;
  showAddButton?: boolean;
  customTooltipPrefix?: string;
  index?: number;
}

/**
 * Default options for card item creation
 */
const DEFAULT_CARD_OPTIONS: CardItemOptions = {
  showPreviewButton: true,
  showAddButton: true,
  customTooltipPrefix: 'Click to Add'
};

/**
 * Builds a card configuration from project data
 *
 * @param project - The project data to build the card from
 * @param options - Optional configuration for card appearance
 * @returns CardOneComponentConfig - The card configuration object
 */
export function buildProjectCard(
  project: Project,
  options: CardItemOptions = DEFAULT_CARD_OPTIONS
): CardOneComponentConfig {
  const cardOptions = { ...DEFAULT_CARD_OPTIONS, ...options };

  // Use project image or fallback to abstract image
  const imageSource = project?.previewImgUrls?.[0] ||
    project?.imagePlaceHolderUrl ||
    (options.index !== undefined ? ABSTRACT_IMAGES[options.index % ABSTRACT_IMAGES.length] : null);

  return {
    data: project,
    image: {
      source: imageSource
    },
    titles: [
      {
        type: HeadingType.H4,
        content: project.title || project.name || 'Untitled Project'
      }
    ],
    imageTags: [{}],
    imageButtons: cardOptions.showPreviewButton ? [{
      icon: {
        name: 'eye',
        type: IconType.NgZorro,
        theme: NgZorroIconTheme.Outline
      },
      customColorSettings: {
        backgroundColor: 'var(--section-background-color)',
        color: 'var(--text-color)'
      },
      shadowSettings: {
        boxShadow: '0 0 0 var(--section-background-color)'
      },
      size: ButtonSize.Small,
      tooltipText: 'Preview Project'
    }] : [],
    descriptions: project.description ? [{
      content: project.description,
      text: project.description,
      textMode: TextMode.Text
    }] : [],
    additionalButtons: cardOptions.showAddButton ? [{
      icon: {
        name: 'fas fa-plus',
        type: IconType.FontAwesome,
        theme: NgZorroIconTheme.Outline
      },
      customColorSettings: {
        backgroundColor: 'var(--section-background-color)',
        color: 'var(--text-color)'
      },
      shadowSettings: {
        boxShadow: '0 0 0 var(--section-background-color)'
      },
      tooltipText: `${cardOptions.customTooltipPrefix} ${project.title || project.name || 'this project'}`
    }] : [],
    useDescriptionIfNoImage: !imageSource,
    additionalTags: project.tags?.map((tag: string) => ({
      text: {
        textMode: TextMode.Text,
        text: tag
      }
    })) || [],
  };
}

/**
 * Builds a "no modules available" placeholder card
 *
 * @returns CardOneComponentConfig - The placeholder card configuration
 */
export function buildNoModulesCard(): CardOneComponentConfig {
  return {
    data: { id: 'no-modules', name: 'No Modules Available' },
    titles: [
      {
        content: 'No Public Modules Available',
        type: defaultToolbarTitleHeadingSize
      }
    ],
    descriptions: [
      {
        text: 'There are currently no public modules available for selection. Please check back later or contact support.',
        textMode: TextMode.Text
      }
    ],
    isSelected: false,
    disabledCard: true
  };
}

/**
 * Builds multiple project cards from an array of projects
 *
 * @param projects - Array of project data
 * @param options - Optional configuration for card appearance
 * @returns CardOneComponentConfig[] - Array of card configurations
 */
export function buildProjectCards(
  projects: Project[],
  options: CardItemOptions = DEFAULT_CARD_OPTIONS
): CardOneComponentConfig[] {
  if (!projects || projects.length === 0) {
    return [buildNoModulesCard()];
  }

  return projects.map((project, index) => buildProjectCard(project, { ...options, index }));
}
